import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Row, Col, Form, Input } from "antd";
import { MdEdit } from "react-icons/md";
import useModal from "../../hooks/useModal";
import { CustomModal, Header, StyledSelect } from "./style";
import { showError, showSuccess } from "infra/helpers/alerta";
import useUpdateDestinatario, { UpdateDestinatarioProps } from "../../hooks/useUpdateDestinatario";
import useCreateDestinatario, { CreateDestinatarioRequest } from "../../hooks/useCreateDestinatario";

interface CreateEditModalProps {
  refetch: () => void;
}

const { Item, useForm } = Form;

function CreateEditModal({ refetch }: CreateEditModalProps) {
  const { isOpen, setIsOpen, isEditing, id, setIsEditing, setId, record } = useModal();
  const { mutateAsync: updateDestinatario } = useUpdateDestinatario();
  const { mutateAsync: createDestinatario } = useCreateDestinatario();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = useForm();

  const defineTitleModal = useMemo((): string => {
    if (isEditing) {
      return "Editar";
    }

    return "Novo";
  }, [isEditing]);

  const prefillForm = useCallback(() => {
    if (isEditing) {
      form.setFieldsValue(record);
      return;
    }

    form.setFieldsValue({});
  }, [record, isEditing, form]);

  useEffect(() => {
    if (!isOpen) return;
    prefillForm();
  }, [record, isOpen, prefillForm]);

  const handleCancel = () => {
    setIsOpen(false);
    setIsEditing(false);
    setId(null);
    form.resetFields();
  }

  const handleUpdate = async (values: any) => {
    if (!id) return;
    setIsSubmitting(true);

    try {
      const body: UpdateDestinatarioProps = {
        notificacaoJustificativasDestinatarioId: id,
        nome: values.nome,
        ativo: values.ativo,
        email: values.email,
        telefone: values.telefone,
      }

      await updateDestinatario(body);

      showSuccess("Destinatário atualizado com sucesso");
      refetch();
      handleCancel();
    } catch (error) {
      showError("Erro ao atualizar destinatário");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreate = async (values: any) => {
    setIsSubmitting(true);

    try {
      const body: CreateDestinatarioRequest = {
        nome: values.nome,
        ativo: values.ativo,
        email: values.email,
        telefone: values.telefone,
      }

      await createDestinatario(body);

      showSuccess("Destinatário criado com sucesso");
      refetch();
      handleCancel();
    } catch (error) {
      showError("Erro ao criar destinatário");
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleSubmit = async (values: any) => {
    if (isEditing) {
      return handleUpdate(values);
    }

    return handleCreate(values);
  }

  return (
    <CustomModal
      visible={isOpen}
      onCancel={handleCancel}
      maskClosable={false}
      width={700}
      title={
        <Header>
          <MdEdit size={18} />
          {defineTitleModal} Destinatário
        </Header>
      }
      footer={
        <>
          <Button
            disabled={isSubmitting}
            loading={isSubmitting}
            size="large"
            onClick={handleCancel}
            style={{ borderRadius: 8 }}
          >Cancelar</Button>
          <Button
            disabled={isSubmitting}
            loading={isSubmitting}
            size="large"
            type="primary"
            htmlType="submit"
            form="job-group-form"
            style={{ borderRadius: 8 }}
          >Salvar</Button>
        </>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        id="job-group-form"
      >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Item
              label="Nome"
              name="nome"
              style={{ margin: 0 }}
              rules={[{ required: true, message: "Nome é obrigatório" }]}
            >
              <Input
                disabled={isSubmitting}
                size="large"
                placeholder="Nome"
                style={{ borderRadius: 8 }}
              />
            </Item>
          </Col>
          <Col span={12}>
            <Item
              label="E-mail"
              name="email"
              style={{ margin: 0 }}
              rules={[{ required: true, message: "E-mail é obrigatório" }]}
            >
              <Input
                disabled={isSubmitting}
                size="large"
                placeholder="E-mail"
                style={{ borderRadius: 8 }}
              />
            </Item>
          </Col>
          <Col span={12}>
            <Item
              label="Telefone"
              name="telefone"
              rules={[{ required: true, message: "Telefone é obrigatório" }]}
            >
              <Input
                disabled={isSubmitting}
                size="large"
                placeholder="Telefone"
                style={{ borderRadius: 8 }}
              />
            </Item>
          </Col>
          <Col span={12}>
            <Item
              label="Status"
              name="ativo"
              rules={[{ required: true, message: "Status é obrigatório" }]}
            >
              <StyledSelect
                disabled={isSubmitting}
                size="large"
                placeholder="Status"
                options={[
                  { label: "Ativo", value: true },
                  { label: "Inativo", value: false },
                ]}
                style={{ borderRadius: 8 }}
              />
            </Item>
          </Col>
        </Row>
      </Form>
    </CustomModal>
  );
}

export default CreateEditModal;
